/**
 * Environmental Charge Scheme Registry — single source of truth.
 *
 * Every environmental/certificate scheme (VEEC, LREC, SREC, ESC, PRC, REPS, EEIS, ...)
 * is declared here once. normalizeCharges.ts, the calculation engine
 * (calculationRoutes.ts / calculationService.ts), offerAutoPopulateService.ts, and the
 * UI (SiteCard.tsx, CompareAndSelectStep.tsx) all read this list instead of hardcoding
 * scheme names, so adding a new scheme only requires one entry here.
 */

export type EnvSchemeDefinition = {
  /** Canonical lowercase short key used in flat rate fields, e.g. `${key}_rate` */
  key: string;
  /** Canonical UI/display key (matches keys used in EnvironmentalCharges / DualRateTable) */
  displayKey: string;
  /** Human label for input rows / table headers */
  label: string;
  /** Lowercase aliases (DB snake_case variants, retailer document abbreviations) that resolve to this scheme */
  aliases: string[];
  /** States this scheme is applicable to; undefined = national/all states */
  states?: string[];
};

export const ENV_SCHEMES: EnvSchemeDefinition[] = [
  // LRET (Large-scale Renewable Energy Target) is the federal SCHEME name; LGC (Large-scale
  // Generation Certificate) is the certificate it issues. Same obligation, two names.
  { key: 'lrec', displayKey: 'LREC', label: 'LREC / LGC', aliases: ['lrec', 'lret', 'lgc'] },
  // SRES (Small-scale Renewable Energy Scheme) is the federal SCHEME name; STC (Small-scale
  // Technology Certificate) is the certificate it issues.
  { key: 'sres', displayKey: 'SREC', label: 'SREC / STC', aliases: ['sres', 'srec', 'sret', 'stc'] },
  // VEET (Victorian Energy Efficiency Target) is VIC's SCHEME name; VEEC (Victorian Energy
  // Efficiency Certificate) is the certificate it issues.
  { key: 'veec', displayKey: 'VEEC/ESS/REES', label: 'VEEC / ESS / REES', aliases: ['veec', 'veec_ess_rees', 'veec/ess/rees', 'veet'] },
  // NSW's "Energy Savings Scheme (ESS)" issues "Energy Savings Certificates (ESC)" — same scheme, two names.
  // "ess" is NOT the same as the VEEC/ESS/REES bucket above — that bucket's "ESS" is a naming collision
  // some retailer docs use loosely; the real NSW ESS always means this ESC scheme.
  { key: 'esc', displayKey: 'ESC', label: 'ESC / ESS', aliases: ['esc', 'esc_rate', 'ess'], states: ['NSW'] },
  // PDRS (NSW Peak Demand Reduction Scheme) is the SCHEME name; PRC (Peak Reduction Certificate)
  // is the certificate it issues.
  { key: 'prc', displayKey: 'PRC', label: 'PRC / PDRS', aliases: ['prc', 'prc_rate', 'pdrs'] },
  // SA's scheme was called REES (Retailer Energy Efficiency Scheme) before a 2021 rebrand to
  // REPS (Retailer Energy Productivity Scheme) — same SA scheme, old name vs current name.
  // "rees" is NOT part of the VEEC bucket above (that was a naming collision in the old codebase).
  { key: 'reps', displayKey: 'REPS', label: 'REPS / REES', aliases: ['reps', 'reps_rate', 'rees'], states: ['SA'] },
  { key: 'eeis', displayKey: 'EEIS', label: 'EEIS', aliases: ['eeis', 'eeis_rate'], states: ['ACT'] },
];

/** Lowercase alias → canonical short key (e.g. 'srec' -> 'sres', 'veec/ess/rees' -> 'veec') */
export const ENV_ALIAS_TO_KEY: Record<string, string> = ENV_SCHEMES.reduce((acc, scheme) => {
  scheme.aliases.forEach(alias => { acc[alias.toLowerCase()] = scheme.key; });
  acc[scheme.key] = scheme.key;
  acc[scheme.displayKey.toLowerCase()] = scheme.key;
  return acc;
}, {} as Record<string, string>);

/** Lowercase alias → canonical display key (e.g. 'srec' -> 'SREC', 'reps' -> 'REPS') */
export const ENV_ALIAS_TO_DISPLAY_KEY: Record<string, string> = ENV_SCHEMES.reduce((acc, scheme) => {
  scheme.aliases.forEach(alias => { acc[alias.toLowerCase()] = scheme.displayKey; });
  acc[scheme.key] = scheme.displayKey;
  acc[scheme.displayKey.toLowerCase()] = scheme.displayKey;
  return acc;
}, {} as Record<string, string>);

export const ENV_SCHEME_BY_KEY: Record<string, EnvSchemeDefinition> = ENV_SCHEMES.reduce((acc, scheme) => {
  acc[scheme.key] = scheme;
  return acc;
}, {} as Record<string, EnvSchemeDefinition>);

/** Resolve any raw key (db snake_case, retailer abbreviation, UI display key) to the canonical short key, or null if unknown */
export const resolveEnvSchemeKey = (rawKey: string): string | null => {
  return ENV_ALIAS_TO_KEY[rawKey.toLowerCase()] ?? null;
};

/** Resolve any raw key to the canonical UI display key (e.g. 'LREC', 'REPS'), or the uppercased raw key if unknown (never drop unrecognized schemes) */
export const resolveEnvDisplayKey = (rawKey: string): string => {
  return ENV_ALIAS_TO_DISPLAY_KEY[rawKey.toLowerCase()] ?? rawKey.toUpperCase();
};

/** Resolve any raw key to its human display label (e.g. 'esc' -> 'ESC / ESS'); falls back to the uppercased raw key for unrecognized/custom schemes so nothing is ever hidden */
export const resolveEnvSchemeLabel = (rawKey: string): string => {
  const key = ENV_ALIAS_TO_KEY[rawKey.toLowerCase()];
  if (key) return ENV_SCHEME_BY_KEY[key].label;
  return rawKey.toUpperCase();
};

/** Schemes applicable to a given Australian state (national schemes + state-specific ones); returns all schemes if state is unknown */
export const getSchemesForState = (state?: string | null): EnvSchemeDefinition[] => {
  if (!state) return ENV_SCHEMES;
  const upper = state.toUpperCase();
  return ENV_SCHEMES.filter(s => !s.states || s.states.includes(upper));
};

// ============================================================================
// Power% defaults — year-wise, single source of truth for the sys_config
// "powerEnvironment" row's shape and how to resolve a value from it.
//
// Previously every consumer (offerAutoPopulateService.ts, calculationRoutes.ts,
// OfferReviewComparison.tsx ×2, normalizeCharges.ts, usePowerEnvDefaults.ts) had its own
// copy-pasted scheme-key-normalization + fallback logic, each with subtly different bugs
// (some fell back to 0, one fell back to VEEC's value, one hardcoded only 3 schemes and
// silently dropped everything else). All of that is replaced by resolvePowerPct() below —
// every consumer now calls this one function instead of reimplementing it.
// ============================================================================

/** Year-wise power% defaults: displayKey (e.g. "LREC", "VEEC/ESS/REES") -> { "2026": 16.67, ... } */
export type PowerEnvYearConfig = Record<string, Record<string, number>>;

/**
 * Resolve the fallback power% for a scheme (by registry displayKey) in a given calendar year.
 *
 * Lookup order: exact year → nearest configured year at or before it → earliest configured year
 * (when the requested year sits before the whole table) → 0.
 *
 * 0 means "no fallback available"; every caller already treats it as "derive from the document
 * alone", which is why it is the right answer when there is nothing honest to return.
 *
 * ⚠️ WITHOUT A YEAR THIS RETURNS 0. IT MUST NOT GUESS.
 *
 * This used to fall through to the most RECENT configured year whenever the calendar year could
 * not be worked out. On a table running 2026-2030 that is 2030 — the far end of the ladder, and
 * the worst available answer for a contract starting in 2026.
 *
 * NEW HORIZON PHOTOBIOTECH is the worked example. Next Business Energy prints environmental
 * RATES and no percentages, so the fallback fills the percentage in. Its SREC year_1 slot lost
 * its date_range in the parse, so the calendar year came back null and this returned SREC's 2030
 * figure, 7.45%, against a slot covering 2027 (12.92%). That percentage is then stored on the
 * offer indistinguishable from one the retailer actually printed, and
 * reconcileEnvRateAgainstCertificate believes it: it discards the printed 0.5168 c/kWh and
 * substitutes 40 x 7.45% = 0.298, putting Next Business's environmental $397/yr under the
 * client's model on every term.
 *
 * Measured on prod: 1,299 slots across 549 offers hold a percentage equal to their scheme's
 * last configured year with no date_range to justify it, and on 1,147 of them (487 offers,
 * 194 tenders) that guess contradicts the rate the document printed.
 *
 * Returning 0 leaves the printed rate standing, which is the figure the retailer actually
 * quoted. A slot that genuinely needs a percentage and genuinely has no date is a parse defect
 * to fix at the source, not something to paper over with the newest row in the table.
 */
export const resolvePowerPct = (
  config: PowerEnvYearConfig | undefined | null,
  displayKey: string,
  year?: number | null
): number => {
  const yearsMap = config?.[displayKey];
  if (!yearsMap) return 0;
  const availableYears = Object.keys(yearsMap)
    .map(y => parseInt(y, 10))
    .filter(y => !isNaN(y))
    .sort((a, b) => a - b);
  if (availableYears.length === 0) return 0;

  if (year == null) return 0;                       // nothing to resolve against — see above

  const exact = yearsMap[String(year)];
  if (exact !== undefined) return exact;

  const lowerOrEqual = availableYears.filter(y => y <= year);
  if (lowerOrEqual.length > 0) return yearsMap[String(lowerOrEqual[lowerOrEqual.length - 1])] ?? 0;

  // The year sits before everything configured, so the EARLIEST row is the nearest one. Reaching
  // past it to the latest year would answer a 2025 slot with 2030's percentage.
  return yearsMap[String(availableYears[0])] ?? 0;
};

// ============================================================================
// Environmental rate unit reconciliation
// ============================================================================

/**
 * The band a real Australian environmental certificate rate falls in, in c/kWh.
 *
 * Measured over the 5,958 prod year-slots whose printed rate AGREES with cert x pct / 1000
 * (ratio 0.8–1.2), i.e. the population known to already be in c/kWh:
 *   1st percentile 0.0430   99th percentile 1.4022   max 2.0126
 * The bounds below sit outside that range on both sides, so a genuine rate is never touched.
 */
const ENV_RATE_PLAUSIBLE_MIN = 0.01;
const ENV_RATE_PLAUSIBLE_MAX = 2.5;

/**
 * Reconcile a document's stated environmental rate against the rate its own certificate
 * price and power percentage imply. Returns the rate to actually charge, in c/kWh.
 *
 * `rate = cert_price x power% / 1000` is definitional, so when both inputs are present the
 * derived value proves the unit. Retailers round their printed rate, so a small divergence is
 * normal and must be left alone — only a CLEAN POWER OF TEN means the parsed figure is in the
 * wrong unit.
 *
 * ⚠️ This previously recognised ONE ratio, 10x, and silently accepted everything else — which
 * let through the far larger case of a rate parsed in $/kWh and charged as if it were c/kWh,
 * i.e. 100x too low. An Engie offer on a 22,000,000 kWh site had its environmental charge
 * stored as $3,130.06 where cert x pct / 1000 gives $313,006.32. The identical 100x error was
 * found and fixed on the CURRENT-CONTRACT side long ago (see environmentalRateInCents and
 * envRateUnits.test.ts, "printed $35 where MAIN TRADING's own model reads $3,462.08"); the
 * offer side never got the same treatment.
 *
 * Ratio ALONE cannot classify these — measured on prod, even same-unit rates diverge from the
 * definitional value by up to 18% (ratio 0.857–1.182), and the $/kWh population spreads across
 * 0.00506–0.01965. So the $/kWh test uses two INDEPENDENT signals instead: the printed rate is
 * implausibly small to be c/kWh, AND the definitional value is plausible. Both must hold.
 *
 * Measured blast radius of the added branch: 16,901 slots across 1,755 offers and 300 tenders,
 * every corrected value landing in 0.0244–2.0126 c/kWh — inside the known-good band. The 116
 * slots sitting between the windows (ratio 0.02–0.8) stay untouched, and the 138 slots caught
 * by the pre-existing 10x branch keep behaving exactly as before.
 *
 * NOT handled on purpose: 49 slots whose rate is in $/MWh (10x low). Their printed values are
 * still plausible as c/kWh, so no signal separates them from a genuine rate — correcting them
 * needs the unit string from the parse, not a heuristic.
 */
/**
 * True when a "power percentage" is really the rate re-parsed into the wrong field.
 *
 * Measured on dev: Next Business Energy's LREC / ESC / SREC slots hold powerPercentage
 * 2.375 / 3.205 / 4.668 against ratePerKwh 0.2375 / 0.3205 / 0.4668 — the rate times ten, to
 * the digit. cert x pct / 1000 built on that gives 0.0338 c/kWh where the true rate is 0.2375,
 * so the derived value must never win against it.
 *
 * The tolerance is deliberately tight. Momentum's LREC carries a legitimate 19.2% against a
 * corrupt rate of 1.999 — ratio 9.605 — and must NOT be caught here, because that is precisely
 * a case where the derived value is the correct one.
 */
const powerPctIsEchoOfRate = (powerPct: number, explicitRate: number): boolean => {
  if (!(powerPct > 0) || !(explicitRate > 0)) return false;
  const ratio = powerPct / explicitRate;
  return Math.abs(ratio - 10) < 0.05 || Math.abs(ratio - 1) < 0.05;
};

export const reconcileEnvRateAgainstCertificate = (
  explicitRate: number,
  certDerivedRate: number,
  documentPowerPct?: number | null,
): number => {
  if (!(explicitRate > 0) || !(certDerivedRate > 0)) return explicitRate;

  // 1. Rate computed as cert x pct / 100 rather than / 1000 — the original guard, unchanged so
  //    the 138 slots it already rescues keep their exact current behaviour.
  if (Math.abs(explicitRate / certDerivedRate - 10) < 0.02) return certDerivedRate;

  // 2. Rate parsed in $/kWh, so charged 100x too low. Two independent signals must agree:
  //    the printed rate is ~1/100 of the definitional one, AND that definitional value is a
  //    plausible c/kWh rate. Nothing genuine sits at a hundredth of its own cert x pct / 1000
  //    — the same-unit population runs 0.857–1.182 of it — while the plausibility test stops a
  //    junk cert price or power percentage from ever overwriting a good rate.
  const ratio = explicitRate / certDerivedRate;
  if (
    ratio >= 0.005 && ratio <= 0.02
    && certDerivedRate >= ENV_RATE_PLAUSIBLE_MIN
    && certDerivedRate <= ENV_RATE_PLAUSIBLE_MAX
  ) {
    return certDerivedRate;
  }

  // 3. The document supplied its OWN power percentage, both figures are plausible c/kWh, and
  //    they still disagree by more than rounding. `rate = cert x pct / 1000` is definitional
  //    and is the formula the client's authoritative model uses, so here the printed rate is
  //    stale and the derived value is the truth.
  //
  //    This fires only when a percentage came from the document itself. A percentage filled in
  //    from the legislated sys_config fallback proves nothing about the retailer's printed
  //    rate, and forcing those was measured to make EXCH PTY LTD's EnergyAustralia and Nectr
  //    WORSE (EnergyAustralia's 24-month environmental moved from exact to +$241) — so callers
  //    must pass the document's value or nothing at all.
  //
  //    Why it is needed: EXCH PTY LTD's Engie offer freezes SREC at 0.4773 c/kWh for years
  //    2, 3 and 4 while its percentages step 12.92 / 9.27 / 8.28. Ratio 1.259 is not a power
  //    of ten, so branches 1 and 2 leave it, and the 48-month environmental came out $9,342
  //    against the client model's $9,047. Deriving all four years reproduces the client model
  //    to the dollar on Engie, Momentum, Next Business and Origin alike.
  //
  //    Measured blast radius on prod: 454 slots across 154 offers and 108 tenders, every
  //    corrected value already inside the known-good 0.0244–1.5076 c/kWh band.
  if (
    documentPowerPct !== undefined
    && documentPowerPct !== null
    && documentPowerPct > 0
    && certDerivedRate >= ENV_RATE_PLAUSIBLE_MIN
    && certDerivedRate <= ENV_RATE_PLAUSIBLE_MAX
    && !powerPctIsEchoOfRate(documentPowerPct, explicitRate)
  ) {
    return certDerivedRate;
  }

  // Anything else is a real disagreement, not a unit slip — leave it exactly as parsed.
  return explicitRate;
};

/**
 * Normalise an environmental rate that has NO certificate price or power percentage to check
 * itself against. Returns c/kWh.
 *
 * reconcileEnvRateAgainstCertificate cannot help here — it works by comparing the printed rate
 * to cert x pct / 1000, and a scheme like REPS supplies neither. REPS is exactly the case that
 * needs this: AGL prints it in §6A as "$0.00335 per kWh" (0.335 c/kWh) while Origin prints its
 * column in $/MWh ("4.910000" = 0.491 c/kWh). Charged verbatim, the first is 100x low and the
 * second 10x high.
 *
 * With no cross-check available, magnitude is the only signal, so this only acts when the
 * printed figure is IMPOSSIBLE as c/kWh and exactly one power-of-ten shift makes it plausible.
 * Measured over prod's rate-only slots (no cert price or no power %):
 *
 *   108 slots /  28 offers — already plausible c/kWh          → untouched
 *   233 slots /  63 offers — x100 lands in band ($/kWh)       → corrected
 *    13 slots /   4 offers — /10 lands in band ($/MWh)        → corrected
 *    28 slots /   4 offers — 32.1–90.06, a certificate price
 *                            parked in the rate field         → untouched
 *
 * This is a heuristic and is deliberately the narrowest one that works. The real fix is for the
 * parse to carry the unit string the document prints; until it does, this stops a rate-only
 * scheme being charged two orders of magnitude out.
 */
export const normalizeEnvRateUnitByMagnitude = (rate: number): number => {
  if (!(rate > 0)) return rate;
  // Already a believable c/kWh figure — never touch it.
  if (rate >= ENV_RATE_PLAUSIBLE_MIN && rate <= ENV_RATE_PLAUSIBLE_MAX) return rate;

  if (rate < ENV_RATE_PLAUSIBLE_MIN) {
    const asCents = rate * 100; // $/kWh -> c/kWh
    if (asCents >= ENV_RATE_PLAUSIBLE_MIN && asCents <= ENV_RATE_PLAUSIBLE_MAX) return asCents;
  } else {
    const asCents = rate / 10; // $/MWh -> c/kWh
    if (asCents >= ENV_RATE_PLAUSIBLE_MIN && asCents <= ENV_RATE_PLAUSIBLE_MAX) return asCents;
  }
  // No single power-of-ten shift makes it plausible — leave it exactly as parsed.
  return rate;
};

/** The calendar year of an ISO-ish date string, or null. */
const yearOfDate = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const y = new Date(value).getFullYear();
  return isNaN(y) ? null : y;
};

const slotRange = (yearData: any) => {
  const r = yearData?.date_range || yearData?.dateRange;
  return {
    start: r?.start_date ?? r?.startDate ?? null,
    end: r?.end_date ?? r?.endDate ?? null,
  };
};

/**
 * True when a scheme's year slots are CUMULATIVE rather than consecutive periods: every slot
 * begins on the contract's first day and is distinguished only by where it ends.
 *
 * ⚠️ THIS IS THE ONLY SHAPE WHERE start_date CANNOT NAME THE YEAR.
 *
 * AGL quotes environmental certificates this way — one whole-of-term price per offer, not one
 * price per compliance year. Dev doc 0d9ba77f, as stored:
 *
 *   year_1  2026-09-01 -> 2026-12-31        year_3  2026-09-01 -> 2028-12-31
 *   year_2  2026-09-01 -> 2027-12-31        year_4  2026-09-01 -> 2029-12-31
 *
 * Every start is 2026-09-01, so reading the stored start gave all four slots the year 2026 and the
 * 2026 LREC percentage — 16.67% four times, where the legislated escalation is
 * 16.67 / 19.17 / 18.98 / 18.79. AGL's parsing prompt stores `power_percentage: null` for every
 * environmental charge ("AGL shows $/Certificate ONLY — no c/kWh column"), so that default is
 * the only source there is, and `rate = cert x pct / 1000` carried the frozen percentage
 * straight into the priced rate.
 *
 * ⚠️ STRADDLING A CALENDAR BOUNDARY IS NOT THE TEST, AND MUST NOT BE.
 *
 * 3,900 prod slots straddle. Most are real consecutive CONTRACT years — 2026-08-01 -> 2027-07-31
 * then 2027-08-01 -> 2028-07-31 (714 AGL, 375 Next Business, 88 Shell slots). Those periods
 * genuinely begin when they say they do; moving them to their end year would misprice a far
 * larger population than the one being fixed. They are excluded here because their starts
 * differ, which is exactly what makes them periods rather than cumulative quotes.
 */
const isCumulativeSchedule = (siblingSlots: Record<string, any> | null | undefined): boolean => {
  if (!siblingSlots || typeof siblingSlots !== 'object') return false;

  const starts = new Set<string>();
  const ends = new Set<string>();
  let slots = 0;

  for (const [key, value] of Object.entries(siblingSlots)) {
    if (!/^year_\d+$/.test(key) || !value || typeof value !== 'object') continue;
    const { start, end } = slotRange(value);
    if (typeof start !== 'string' || start === '') return false;  // a slot with no start proves nothing
    slots += 1;
    starts.add(start);
    if (typeof end === 'string' && end !== '') ends.add(end);
  }

  // Two or more slots, one shared start, and ends that actually distinguish them. Without
  // differing ends there is nothing for the end date to resolve that the start does not.
  return slots >= 2 && starts.size === 1 && ends.size >= 2;
};

const MS_PER_DAY = 86_400_000;

/** An ISO-ish date as a UTC timestamp, or null. */
const utcMsOf = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
};

/**
 * The year a CUMULATIVE slot's own period begins in — the day after the previous slot ends.
 *
 * ⚠️ WHY NOT THE END YEAR, WHICH IS WHAT THIS USED TO RETURN.
 *
 * A cumulative slot is a to-date figure: slot N covers the whole contract so far. What slot N
 * actually PRICES is the stretch the previous slot did not — from the day after it ended to the
 * day this one ends. That stretch is the compliance year the percentage belongs to.
 *
 * The end date only names it when the slots end on 31 December, which is why the end-year rule
 * looked right. AGL on PRINCE FABRICATIONS ends every slot on 31 Dec:
 *
 *   year_1  2026-09-01 -> 2026-12-31      year_3  2026-09-01 -> 2028-12-31
 *   year_2  2026-09-01 -> 2027-12-31      year_4  2026-09-01 -> 2029-12-31
 *
 * End years 2026/2027/2028/2029; incremental starts 2026-09-01, 2027-01-01, 2028-01-01,
 * 2029-01-01 — the same four years. Both rules agree, and that tender is unchanged.
 *
 * AGL on COWES BAKEHOUSE ends every slot on 31 March, and they part company:
 *
 *   year_1  2026-04-09 -> 2027-03-31      year_3  2026-04-09 -> 2029-03-31
 *   year_2  2026-04-09 -> 2028-03-31      year_4  2026-04-09 -> 2030-03-31
 *
 * The end years are 2027/2028/2029/2030, giving SREC 12.92 / 9.27 / 8.28 / 7.45. But year_1
 * prices 09/04/2026 - 31/03/2027, of which 266 days are in 2026 and 90 in 2027; the client's
 * report prices it as 2026 and reads 11.67 / 11.33 / 9.27 / 8.28. Every year was one ahead,
 * and AGL's environmental came out $129 high on a $19,281 year-1 total. Incremental starts —
 * 2026-04-09, 2027-04-01, 2028-04-01, 2029-04-01 — reproduce the client's four exactly.
 *
 * The first slot has no predecessor, so its period begins at the contract start the whole
 * schedule shares. That makes this rule the same statement as the non-cumulative one: a slot
 * belongs to the year its own period opens in.
 */
const cumulativePeriodYear = (
  slotEnd: string | null,
  sharedStart: string | null,
  siblingSlots: Record<string, any>,
): number | null => {
  const endMs = utcMsOf(slotEnd);
  if (endMs === null) return null;              // no end: the caller falls back to the start

  let previousEnd: number | null = null;
  for (const [key, value] of Object.entries(siblingSlots)) {
    if (!/^year_\d+$/.test(key) || !value || typeof value !== 'object') continue;
    const siblingMs = utcMsOf(slotRange(value).end);
    if (siblingMs === null || siblingMs >= endMs) continue;
    if (previousEnd === null || siblingMs > previousEnd) previousEnd = siblingMs;
  }

  const periodStart = previousEnd === null ? utcMsOf(sharedStart) : previousEnd + MS_PER_DAY;
  if (periodStart === null) return null;
  return new Date(periodStart).getUTCFullYear();
};

/**
 * The calendar year a parsed environmental year-slot belongs to.
 *
 * A slot belongs to the year its own period OPENS in. For an ordinary schedule that is its
 * `date_range.start_date`. Pass the scheme's other slots as `siblingSlots` and a CUMULATIVE
 * schedule (see isCumulativeSchedule) is resolved by where its own period opens — the day
 * after the previous slot ends — because every cumulative slot shares one stored start.
 *
 * `siblingSlots` is optional so every existing caller keeps its exact behaviour; callers that
 * have the scheme to hand pass it and get the corrected year.
 */
export const extractCalendarYear = (
  yearData: any,
  siblingSlots?: Record<string, any> | null,
): number | null => {
  const { start, end } = slotRange(yearData);

  if (isCumulativeSchedule(siblingSlots)) {
    const periodYear = cumulativePeriodYear(end, start, siblingSlots!);
    if (periodYear !== null) return periodYear;   // a slot missing its end falls back to the start
  }
  return yearOfDate(start);
};

/**
 * The term's average ANNUAL environmental cost, from each contract year's cost and weight.
 *
 * ⚠️ A YEAR THE RETAILER DID NOT PRICE IS A ZERO, NOT AN ABSENCE — UNLESS IT IS PAST THE END
 * OF WHAT THEY QUOTED.
 *
 * Two different things produce a zero-cost year, and they need opposite treatment:
 *
 *   LEADING / INTERIOR GAP — the term covers a period the retailer left blank. Flo and Next
 *     Business quote no 2026 environmental row at all, starting at 2027. Their 16-month term
 *     still RUNS through 2026; the retailer simply levies nothing for it. The client model
 *     counts that year as $0 and still divides by it, so the average is genuinely halved.
 *
 *   PHANTOM TAIL — the term was given more year slots than the document has years. A 52-month
 *     term gets ceil(52/12) = 5 while the offer quotes four. That fifth slot describes nothing;
 *     dividing a real four-year cost by five understates every such term.
 *
 * Dropping both — which is what this code did in both of its copies — made Next Business's
 * 16-month environmental print as $0 against the client model's $1,667, and Flo's 28-month as
 * $5,639 against $3,899: the offer's own later-year rates applied across the whole term with
 * nothing averaging them down. Counting both would reintroduce the 52-month understatement.
 *
 * So: everything up to and including the LAST year that carries a real cost is averaged, zeros
 * and all; anything beyond it is ignored. Verified against WERRIBEE RACING CLUB's tender report
 * on all five affected figures — Flo 16/28/40mo = 1,548 / 3,899 / 6,493 and Next Business
 * 16/28mo = 1,667 / 3,614 — each reproduced to the dollar, and none of them by either of the
 * other two rules.
 *
 * ⚠️ ONE FUNCTION, TWO CALLERS. calculationRoutes prices a single offer and a whole tender
 * through separate loops, and each had its own copy of this rule. Fixing the single-offer one
 * left the tender-wide recalculation — the path "Recalculate" and document generation actually
 * use — still averaging the old way, so the corrected figure never reached a single document.
 * Both now call this.
 *
 * @param perYear One entry per contract year, in year order, with that year's ANNUAL cost and
 *                its share of the whole-of-term multiplier.
 */
export const averageEnvAcrossYears = (
  perYear: Array<{ weight: number; cost: number }>,
): number => {
  let lastPricedIndex = -1;
  perYear.forEach((entry, index) => { if (entry.cost > 0) lastPricedIndex = index; });
  if (lastPricedIndex < 0) return 0;

  let sum = 0;
  let weight = 0;
  for (let i = 0; i <= lastPricedIndex; i++) {
    sum += perYear[i].cost * perYear[i].weight;
    weight += perYear[i].weight;
  }
  return weight > 0 ? sum / weight : 0;
};
